import { useRef } from "react";
import { Upload, X } from "lucide-react";

import { Button } from "../../../button";
import { OverflowDisplayTooltip } from "../../../Tooltip/OverflowDisplayTooltip";
import { getFileSizeFromBase64 } from "../../utils";

import { cn } from "@/lib/utils";

interface FileDropzoneProps {
  isDragOver: boolean;
  isUploading: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Single file mode (legacy - for base64 conversion)
  uploadedFile?: File | null;
  fileUrl?: string;
  onRemoveFile?: () => void;

  // Multi-file mode (for vector stores)
  selectedFiles?: File[];
  onRemoveFileAtIndex?: (index: number) => void;

  // Configuration
  acceptedFormats?: string;
  multiple?: boolean;
  loadingMessage?: string;
  helperText?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

export const FileDropzone = ({
  isDragOver,
  isUploading,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInputChange,
  uploadedFile,
  fileUrl,
  onRemoveFile,
  selectedFiles = [],
  onRemoveFileAtIndex,
  acceptedFormats = "*/*",
  multiple = false,
  loadingMessage = "Converting to base64...",
  helperText = "Files will be converted to base64 format",
}: FileDropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Legacy single-file mode display
  if (uploadedFile && !multiple) {
    return (
      <div className="flex items-center justify-between rounded-md bg-green-50 p-3">
        <div className="flex flex-1 items-center space-x-2">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <div className="flex flex-1 items-center space-x-2">
            <div className="w-fit max-w-[150px] overflow-hidden text-sm font-medium text-green-800">
              <OverflowDisplayTooltip content={uploadedFile.name} className="w-full max-w-[150px]">
                <span>{uploadedFile.name}</span>
              </OverflowDisplayTooltip>
            </div>
            <span className="text-sm text-green-600">
              ({fileUrl ? getFileSizeFromBase64(fileUrl) : formatFileSize(uploadedFile.size)})
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemoveFile}
          className="h-6 w-6 p-0 text-green-700 hover:bg-green-100"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Dropzone Area */}
      <div
        className={cn(
          "relative rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          isDragOver
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400 hover:bg-gray-50",
        )}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileInputChange}
          accept={acceptedFormats}
          multiple={multiple}
        />

        {isUploading ? (
          <div className="flex flex-col items-center space-y-2">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <p className="text-sm text-gray-600">{loadingMessage}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <div className="space-y-1">
              <p className="text-sm text-gray-600">
                Drag and drop {multiple ? "files" : "a file"} here, or{" "}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  browse
                </button>
              </p>
              {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Multi-file mode: Selected Files List */}
      {multiple && selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Selected Files ({selectedFiles.length})
          </p>
          <div className="max-h-32 space-y-1 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${file.name}-${index}`}
                className="flex items-center justify-between rounded-md bg-blue-50 p-2"
              >
                <div className="flex flex-1 items-center space-x-2 overflow-hidden">
                  <div className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                  <span className="truncate text-sm font-medium text-blue-800">{file.name}</span>
                  <span className="flex-shrink-0 text-xs text-blue-600">
                    ({formatFileSize(file.size)})
                  </span>
                </div>
                {onRemoveFileAtIndex && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveFileAtIndex(index)}
                    className="h-6 w-6 flex-shrink-0 p-0 text-blue-700 hover:bg-blue-100"
                    disabled={isUploading}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
